import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, Play, Upload } from 'lucide-react'
import { useState, type FormEvent } from 'react'

import { api, request } from '@/api/client'
import type { LibraryItem, MeasuredAudioProperties } from '@/api/types'
import { toQueueItem, usePlayer } from '@/audio/store'
import { Button, Dialog, Input, toast } from '@/components/ui'
import { ErrorNotice } from '@/components/ui/Feedback'
import { ToggleField } from '@/features/settings/ToggleField'
import { formatBytes } from '@/lib/format'

import { qualityDescription } from './audioQuality'
import { errorMessage } from './errorMessage'

type EditableMetadata = { title: string; artist_name: string; album_name: string }
type MetadataDocument = {
  metadata: EditableMetadata
  original_filename?: string
  sha256?: string
  original_tags_modified: false
}
type Rendition = { url: string; format: string; measured_properties: MeasuredAudioProperties; original_preserved: boolean }

export function TrackDetailsDialog({ item, onClose }: { item: LibraryItem; onClose: () => void }) {
  const queryClient = useQueryClient()
  const path = `/library/tracks/${item.id}`
  const [draft, setDraft] = useState<EditableMetadata | null>(null)
  const [sidecar, setSidecar] = useState<File | null>(null)
  const [replacement, setReplacement] = useState<File | null>(null)
  const [confirmed, setConfirmed] = useState(false)
  const [allowLossy, setAllowLossy] = useState(false)
  const metadata = useQuery({
    queryKey: ['library-metadata', item.id],
    queryFn: () => api.get<MetadataDocument>(`${path}/metadata`),
  })
  const current = draft ?? metadata.data?.metadata ?? {
    title: item.track.title, artist_name: item.track.artist_name, album_name: item.track.album_name ?? '',
  }
  const invalidate = () => {
    for (const key of ['library', 'library-albums', 'library-album', 'library-metadata', 'album', 'search', 'artist', 'playlist', 'history', 'storage', 'similar', 'daylists', 'artists', 'artist-search', 'album-search']) {
      void queryClient.invalidateQueries({ queryKey: [key] })
    }
  }
  const edit = useMutation({
    mutationFn: (values: EditableMetadata) => api.patch<MetadataDocument>(`${path}/metadata`, values),
    onSuccess: (result) => {
      queryClient.setQueryData(['library-metadata', item.id], result)
      setDraft(null)
      invalidate()
      toast.success('Metadata saved', 'Original audio and file tags are preserved.')
    },
  })
  const restore = useMutation({
    mutationFn: () => {
      if (!sidecar || sidecar.size > 65536) throw new Error('Choose a metadata JSON file up to 64 KiB.')
      const body = new FormData()
      body.append('file', sidecar)
      return api.post<MetadataDocument>(`${path}/metadata`, body)
    },
    onSuccess: () => {
      setDraft(null); setSidecar(null); invalidate()
      toast.success('Metadata restored', 'The original checksum matched. Audio and file tags are preserved.')
    },
  })
  const upgrade = useMutation({
    mutationFn: () => {
      const body = new FormData()
      body.append('file', replacement!)
      body.append('confirm_same_recording', String(confirmed))
      body.append('allow_lossy', String(allowLossy))
      return request<LibraryItem>(`${path}/upgrade`, { method: 'POST', body })
    },
    onSuccess: () => {
      invalidate()
      toast.success('Original upgraded', 'Playlists, favourites and history are preserved.')
      onClose()
    },
  })
  const rendition = useMutation({
    mutationFn: () => api.post<Rendition>(`${path}/rendition`, {}),
    onSuccess: () => {
      invalidate()
      toast.success('Compatibility copy ready', 'Your original file is preserved.')
    },
  })
  const busy = edit.isPending || restore.isPending || upgrade.isPending || rendition.isPending
  function save(event: FormEvent) {
    event.preventDefault()
    if (!current.title.trim() || !current.artist_name.trim()) return
    edit.mutate(current)
  }
  function change(field: keyof EditableMetadata, value: string) {
    setDraft({ ...current, [field]: value })
  }

  return <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose() }} title="Song details" description="Your stored original, playback options and library metadata." size="lg">
    <div className="space-y-6">
      <section aria-label="Original audio">
        <h3 className="font-medium text-text">Original audio</h3>
        <p className="mt-1 text-sm text-text-muted">{qualityDescription(item.audio_properties?.measured)}</p>
        <p className="mt-1 text-xs text-text-muted">{formatBytes(item.file_size_bytes)} · Source: {item.track.provider}</p>
        <p className="mt-1 text-xs text-text-muted">Mastering provenance: {item.audio_properties?.provenance ?? 'unknown'}. A lossless codec does not prove the original master was lossless.</p>
        {metadata.data?.original_filename ? <p className="mt-2 break-all text-xs text-text-muted">{metadata.data.original_filename}</p> : null}
        {item.status === 'ready' ? <div className="mt-3 flex flex-wrap gap-3">
          <a className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-border px-3 text-sm font-medium hover:bg-surface-hover" href={item.export_url ?? `/api/v1${path}/export`} download><Download className="h-4 w-4" aria-hidden="true" />Export original</a>
          <a className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-border px-3 text-sm font-medium hover:bg-surface-hover" href={item.metadata_url ?? `/api/v1${path}/metadata`} download={`${item.track.title}.json`}>Export metadata</a>
        </div> : null}
      </section>

      <form onSubmit={save} className="space-y-3" aria-label="Library metadata">
        <h3 className="font-medium text-text">Library metadata</h3>
        <p className="text-sm text-text-muted">Edits apply to your library. Original file tags stay intact.</p>
        {metadata.isError ? <ErrorNotice onRetry={() => void metadata.refetch()}>Metadata could not be loaded. You can still edit the values shown.</ErrorNotice> : null}
        <Input label="Title" value={current.title} required maxLength={255} onChange={(event) => change('title', event.target.value)} disabled={busy} />
        <Input label="Artist" value={current.artist_name} required maxLength={255} onChange={(event) => change('artist_name', event.target.value)} disabled={busy} />
        <Input label="Album" value={current.album_name} maxLength={255} onChange={(event) => change('album_name', event.target.value)} disabled={busy} />
        {edit.isError ? <ErrorNotice>{errorMessage(edit.error)}</ErrorNotice> : null}
        <Button type="submit" loading={edit.isPending} disabled={!draft || busy || !current.title.trim() || !current.artist_name.trim()}>Save metadata</Button>
      </form>

      <section className="space-y-3" aria-label="Restore metadata">
        <h3 className="font-medium text-text">Restore exported metadata</h3>
        <p className="text-sm text-text-muted">After importing an exported original, choose its Tunes JSON sidecar to restore your edits. The audio checksum must match.</p>
        <Input type="file" label="Metadata JSON sidecar" accept=".json,application/json" disabled={busy} onChange={event => setSidecar(event.target.files?.[0] ?? null)} />
        {restore.isError ? <ErrorNotice>{errorMessage(restore.error)}</ErrorNotice> : null}
        <Button variant="secondary" loading={restore.isPending} disabled={busy || !sidecar} onClick={() => restore.mutate()}>Restore metadata</Button>
      </section>

      <section className="space-y-3" aria-label="Playback compatibility">
        <h3 className="font-medium text-text">Playback compatibility</h3>
        <p className="text-sm text-text-muted">Create a separate AAC copy if your browser cannot play the original. This does not upgrade the original’s quality.</p>
        {rendition.data ? <>
          <p className="text-sm text-text-muted">Playback copy: {qualityDescription(rendition.data.measured_properties)}</p>
          <Button variant="secondary" onClick={() => {
            const playable = toQueueItem({ ...item, stream_url: rendition.data!.url })
            if (playable) usePlayer.getState().playQueue([playable], 0, { source: 'library' })
          }}><Play className="h-4 w-4" aria-hidden="true" />Play compatibility copy</Button>
        </> : <Button variant="secondary" loading={rendition.isPending} disabled={busy || item.status !== 'ready'} onClick={() => rendition.mutate()}>Create compatibility copy</Button>}
        {rendition.isError ? <ErrorNotice>{errorMessage(rendition.error)}</ErrorNotice> : null}
      </section>

      <section className="space-y-3" aria-label="Upgrade original">
        <h3 className="font-medium text-text">Upgrade original</h3>
        <p className="text-sm text-text-muted">Choose another file of this recording and edition. It is validated before replacement; the previous original is retained for recovery.</p>
        <Input type="file" label="Replacement audio file" accept=".flac,.m4a,.mp3" disabled={busy} onChange={(event) => { setReplacement(event.target.files?.[0] ?? null); setConfirmed(false) }} />
        <ToggleField label="This is the same recording and edition" checked={confirmed} onChange={setConfirmed} disabled={busy || !replacement} />
        <ToggleField label="Allow a lossy replacement" description="Your required-lossless setting still takes precedence." checked={allowLossy} onChange={setAllowLossy} disabled={busy} />
        {upgrade.isError ? <ErrorNotice>{errorMessage(upgrade.error)}</ErrorNotice> : null}
        <Button variant="secondary" loading={upgrade.isPending} disabled={busy || !replacement || !confirmed} onClick={() => upgrade.mutate()}><Upload className="h-4 w-4" aria-hidden="true" />Validate and upgrade</Button>
      </section>
    </div>
  </Dialog>
}
